import { useState, useCallback, useEffect } from 'react';
import { fryApiClient } from 'src/clients/api-client';
import { Logger } from 'src/utils/logger';
import { useDebounce } from './useDebounce';
import { useTranslation } from 'src/arb-wrapper';
import type { CampaignDesiredStateDto, Country } from 'src/store/generated/leela-api-generated';

export interface ProductSetValidationParams {
  publisher: CampaignDesiredStateDto['publisher'];
  productSetId: string;
  country: Country;
  businessLine?: string;
}

export interface ProductSetValidationResult {
  isValid: boolean;
  error?: string;
}

export interface ProductSetValidationState {
  isLoading: boolean;
  isValid: boolean | null;
  error: string | null;
}

export interface UseProductSetValidationReturn {
  validateProductSet: (params: ProductSetValidationParams) => Promise<ProductSetValidationResult>;
  validationState: ProductSetValidationState;
  resetValidation: () => void;
  validateProductSetDebounced: (params: ProductSetValidationParams | null) => void;
  isDebouncing: boolean;
}

export const useProductSetValidation = (): UseProductSetValidationReturn => {
  const { t } = useTranslation();
  const [validationState, setValidationState] = useState<ProductSetValidationState>({
    isLoading: false,
    isValid: null,
    error: null,
  });

  const [validationParams, setValidationParams] = useState<ProductSetValidationParams | null>(null);
  const [debouncedParams, isDebouncing] = useDebounce(validationParams, 500);

  const resetValidation = useCallback(() => {
    setValidationState({
      isLoading: false,
      isValid: null,
      error: null,
    });
    setValidationParams(null);
  }, []);

  const validateProductSetDebounced = useCallback((params: ProductSetValidationParams | null) => {
    if (params?.productSetId && params?.publisher && params?.country) {
      setValidationState(prev => ({ ...prev, isLoading: true, error: null }));
    }
    setValidationParams(params);
  }, []);

  const validateProductSet = useCallback(async (params: ProductSetValidationParams): Promise<ProductSetValidationResult> => {
    const { publisher, productSetId, country, businessLine } = params;

    setValidationState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const apiParams: Record<string, string> = {
        publisher: publisher.toUpperCase(),
        productSetId,
        country: country.toUpperCase(),
      };

      if (businessLine) {
        apiParams.businessLine = businessLine.toUpperCase();
      }

      const response = await fryApiClient.get('/validate-product-set', {
        params: apiParams,
      });

      const isValid = response.data === true;
      setValidationState({ isLoading: false, isValid, error: null });
      return { isValid };
    } catch (err: unknown) {
      Logger.error('Product set validation failed:', err instanceof Error ? err : new Error(String(err)));

      let errorMessage = t('product_set_validation_failed');

      // Check if error has the expected structure before accessing properties
      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as { response?: { status?: number; data?: { error?: string; message?: string } } }).response;
        if (response?.status === 400) {
          errorMessage = response.data?.error ?? t('product_set_validation_invalid_parameters');
        } else if (response?.status === 403) {
          errorMessage = response.data?.message ?? t('product_set_validation_auth_failed');
        } else if (response?.status === 500) {
          errorMessage = t('product_set_validation_service_unavailable');
        }
      }

      setValidationState({ isLoading: false, isValid: false, error: errorMessage });
      return { isValid: false, error: errorMessage };
    }
  }, []);

  useEffect(() => {
    if (debouncedParams?.productSetId && debouncedParams?.publisher && debouncedParams?.country) {
      const validParams: ProductSetValidationParams = {
        publisher: debouncedParams.publisher,
        productSetId: debouncedParams.productSetId,
        country: debouncedParams.country,
        businessLine: debouncedParams.businessLine,
      };
      void validateProductSet(validParams);
    } else if (debouncedParams === null || !debouncedParams?.productSetId) {
      setValidationState({ isLoading: false, isValid: null, error: null });
    }
  }, [debouncedParams, validateProductSet]);

  return {
    validateProductSet,
    validationState,
    resetValidation,
    validateProductSetDebounced,
    isDebouncing,
  };
};
